import React from 'react';
import { Card } from '../../../../../components/ui/card';
import {
  Calendar,
  MapPin,
  User,
  Building2,
  Activity,
  Wrench,
  Cable,
  FileText,
  Shield,
  Flame,
  CheckCircle,
  XCircle
} from 'lucide-react';
import type { Project } from '../../../types/project';
import { BasicInfo } from './components/BasicInfo';
import { PipelineTypeSection } from './components/PipelineTypeSection';
import { FabricationTypeSection } from './components/FabricationTypeSection';
import { OtherTypeSection } from './components/OtherTypeSection';
import { ProjectTypeBadge, StatusIcon } from './components/common';


interface ProjectOverviewProps {
  project: Project;
}

export const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project }) => {
  const getStatusColor = (status: string) => {
    const colors = {
      'active': 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
      'completed': 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
      'on-hold': 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
      'cancelled': 'text-readable-red bg-[rgba(196,0,31,0.06)]'
    } as const;
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProjectTypeStatus = (enabled: boolean) => {
    return <StatusIcon enabled={enabled} />;
  };

  return (
    <div className="space-y-6 space-x-6">
      {/* Basic Information */}
      <BasicInfo project={project} />

      {/* Project Types */}
      <Card className="p-6" style={{ marginLeft: '20px', marginRight: '20px', marginBottom: '20px' }}>
        <h3 className="text-[14px] font-semibold mb-6">Project Types</h3>
        <div className="space-y-8">
          {/* Project Type Badges */}
          <div>
            <p className="text-sm text-gray-500 mb-3">Selected Types</p>
            <div className="flex flex-wrap gap-2">
              {project.projectTypes.pipeline?.enabled && (
                <ProjectTypeBadge
                  icon={Cable}
                  label="Pipeline"
                  bgColor="bg-blue-100"
                  textColor="text-blue-800"
                />
              )}
              {project.projectTypes.fabrication?.enabled && (
                <ProjectTypeBadge
                  icon={Wrench}
                  label="Fabrication"
                  bgColor="bg-purple-100"
                  textColor="text-purple-800"
                />
              )}
              {project.projectTypes.others?.enabled && (
                <ProjectTypeBadge
                  icon={Activity}
                  label="Others"
                  bgColor="bg-lime-100"
                  textColor="text-lime-800"
                />
              )}
              {!project.projectTypes.pipeline?.enabled && !project.projectTypes.fabrication?.enabled && !project.projectTypes.others?.enabled && (
                <ProjectTypeBadge
                  icon={XCircle}
                  label="No project types enabled"
                  bgColor="bg-gray-100"
                  textColor="text-gray-500"
                />
              )}
            </div>
          </div>

          {/* Project Type Details */}
          <div className="space-y-4">
            {/* Pipeline Type */}

            {project.projectTypes.pipeline?.enabled && (
              <PipelineTypeSection project={project} />
            )}





            {project.projectTypes.fabrication?.enabled &&
              <FabricationTypeSection project={project} />
            }


            {project.projectTypes.others?.enabled &&
              <OtherTypeSection project={project} />
            }

          </div>
        </div>
      </Card>
    </div>
  );
};